import { useState } from "react";
import { getConsent, setConsent } from "../analytics/posthog";
import "./ConsentBanner.css";
import { useI18n } from "../i18n";

/**
 * Cookie consent banner (PECR / UK GDPR) — two-tier model.
 *
 * Cookie-free, anonymous per-visit analytics always runs (nothing stored on
 * the device → no cookie consent required; legitimate-interests basis,
 * disclosed here and in the privacy notice). This banner asks ONLY about
 * cookies, which let us link visits (accurate visitor counts). Declining
 * stays cookie-free. The choice is remembered so the banner shows once.
 * Wording to be confirmed by the DPO before go-live.
 */
export function ConsentBanner() {
  const { t } = useI18n();
  const [choice, setChoice] = useState(getConsent());
  if (choice !== null) return null;

  const decide = (c: "granted" | "denied") => {
    setConsent(c);
    setChoice(c);
    // The banner sits at the end of the DOM. Removing the button that had
    // focus leaves the browser's sequential-focus starting point down there,
    // so the next Tab landed on the accessibility tab instead of the skip
    // link (external audit 4 Sept 2026, WCAG 2.4.1/2.4.3). Reset it to the
    // top of the page: focus the app shell (tabIndex -1, no outline) so the
    // next Tab is the skip link. Nothing visible changes for mouse users.
    requestAnimationFrame(() => {
      const shell = document.querySelector<HTMLElement>(".app-shell");
      shell?.focus({ preventScroll: true });
    });
  };

  return (
    <section className="consent-banner" role="region" aria-label="Cookies and analytics">
      <p className="consent-banner-text">
        We collect anonymous usage information to improve this service — never
        what you type. By default this uses no cookies and nothing is stored on
        your device. Accepting cookies additionally lets us count visitors
        accurately. Everything works either way.
      </p>
      <div className="consent-banner-actions">
        <button type="button" className="consent-btn consent-btn-accept" onClick={() => decide("granted")}>
          {t("consent.accept")}
        </button>
        <button type="button" className="consent-btn consent-btn-decline" onClick={() => decide("denied")}>
          {t("consent.decline")}
        </button>
      </div>
    </section>
  );
}
